import { Uuid } from "@/admin/AdminTable.tsx";
import { AdminTitle } from "@/admin/AdminTitle.tsx";
import { CleanTrashButton } from "@/admin/buckets/CleanTrashModal.tsx";
import { adminStoragePoolQueryAtom } from "@/admin/buckets/storagePoolsQuery.ts";
import { LocationItem } from "@/admin/table_fields/LocationItem.tsx";
import { WAdminTable } from "@/admin/WAdminTable.tsx";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { WFakeRadio } from "@/components/forms/WFakeRadio.tsx";
import { Percent } from "@/components/l10n/Percent.tsx";
import { WTableBody } from "@/components/table/WTable.tsx";
import { ErrorRow } from "@/modules/error/ErrorFallback.tsx";
import { queryAtom } from "@/modules/location/locationQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useAtomValue } from "jotai/react";

export function StoragePoolsView() {
    const locations = useSuspenseQueryAtom(queryAtom);

    const table = (
        <WAdminTable>
            <thead>
                <tr>
                    <th>Name</th>
                    <th>UUID</th>
                    <th>Description</th>
                    <th>
                        <div className="flex justify-center">Is default</div>
                    </th>
                    <th>Used</th>
                    <th>Stored</th>
                    <th>Total</th>
                    <th>Trash images</th>
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {locations.map(({ slug }) => (
                    <StoragePoolRow key={slug} location={slug} />
                ))}
            </WTableBody>
        </WAdminTable>
    );

    return (
        <>
            <AdminTitle title="Storage Pools" />
            {table}
        </>
    );
}

function StoragePoolRow({ location }: { location: string }) {
    const { error: storageError, data: storagePools } = useAtomValue(adminStoragePoolQueryAtom(location));

    return (
        <>
            <tr>
                <td colSpan={99} className="text-center bg-grey-1">
                    <LocationItem value={location} />
                </td>
            </tr>
            {storageError ? (
                <tr>
                    <td colSpan={99} className="text-left bg-error-light p-0!">
                        {storageError && <ErrorRow error={storageError} tags="HostPoolsTable, storagePools" />}
                    </td>
                </tr>
            ) : (
                !!storagePools?.size &&
                [...storagePools.values()].map((item) => {
                    const { display_name, uuid, pool_name, is_default, percent_used, stored_gb, total_gb, stats } =
                        item;

                    return (
                        <tr key={uuid}>
                            <td>{display_name}</td>
                            <td>
                                <ClipBoardTooltip isHtml hideIcon>
                                    <Uuid value={uuid} />
                                </ClipBoardTooltip>
                            </td>
                            <td>{pool_name}</td>
                            <td>
                                <div className="flex justify-center">
                                    <WFakeRadio isSelected={is_default} />
                                </div>
                            </td>
                            <td>
                                <Percent value={percent_used} maximumFractionDigits={1} />
                            </td>
                            <td>{stored_gb} GB</td>
                            <td>{total_gb} GB</td>
                            <td>{stats?.trash_items_count ?? "-"}</td>
                            <td className="text-right">
                                <CleanTrashButton
                                    item={item}
                                    location={location}
                                    button={<WModalButton label="Clean trash" inTable icon="i-lucide:eraser" />}
                                />
                            </td>
                        </tr>
                    );
                })
            )}
        </>
    );
}
